/**
 *
 * Copyright © 2026 Ping Identity Corporation. All right reserved.
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file for details.
 *
 **/

import { oidc } from '@forgerock/oidc-client';
import { writable } from 'svelte/store';
import { z } from 'zod';

import type {
  CustomLogger,
  GenericError,
  LogLevel,
  OidcClient,
  RequestMiddleware,
  StorageConfig,
} from '@forgerock/oidc-client/types';
import type { Readable } from 'svelte/store';

/**
 * Validates the OIDC client config passed to the widget. Accepts the fields from
 * OidcConfig that have a clear consumer use case. serverConfig is injected by the
 * widget from its own wellknown; log is omitted in favour of the top-level logger option.
 */
export const oidcClientConfigSchema = z
  .object({
    clientId: z.string(),
    redirectUri: z.string().url({
      message: '`redirectUri` must be a full URL (this is generated by the Zod library).',
    }),
    scope: z.string().default('openid'),
    serverConfig: z
      .object({
        wellknown: z
          .string({
            error: (issue) =>
              issue.input === undefined
                ? 'Setting the `serverConfig.wellknown` is required (this is generated by the Zod library).'
                : '`serverConfig.wellknown` must be a URL string (this is generated by the Zod library).',
          })
          .url({
            message:
              '`serverConfig.wellknown` must be a full URL (this is generated by the Zod library).',
          }),
      })
      .strict(),
    par: z.boolean().optional(),
    loginHint: z.string().optional(),
    acrValues: z.string().optional(),
    query: z.record(z.string(), z.string()).optional(),
    oauthThreshold: z.number().optional(),
  })
  .strict();

export type OidcClientConfig = z.infer<typeof oidcClientConfigSchema>;

export type OidcClientStore = Readable<OidcClient | null> & {
  /**
   * Resolves with the constructed client (or a `GenericError` if construction
   * failed). Awaiting this is the readiness gate — the store is already
   * populated by the time it resolves, so callers never see the `null` window.
   */
  getClient: () => Promise<OidcClient>;
};

/**
 * @function createOidcClientStore
 *
 * Wraps the `oidc()` factory in a Svelte store. The client is constructed once;
 * `getClient()` shares that single construction promise so awaiting it is the
 * readiness gate. `subscribe` exposes the same client reactively — `null` until
 * construction settles, then the resolved client (or a `GenericError`).
 * Inject the returned store into OAuth and User stores so both share the same
 * client instance without module-level state.
 *
 * @param {OidcClientConfig} config — OIDC client configuration (validated by Zod).
 * @param {RequestMiddleware[]} [requestMiddleware] — optional request middleware forwarded to `oidc()`.
 * @param {{ level: LogLevel; custom?: CustomLogger }} [logger] — optional logger (level + custom sink) forwarded to `oidc()`.
 * @param {StorageConfig} [storage] — optional token storage config forwarded to `oidc()`.
 * @returns {OidcClientStore}
 */
export function createOidcClientStore(
  config: OidcClientConfig,
  requestMiddleware?: RequestMiddleware[],
  logger?: { level: LogLevel; custom?: CustomLogger },
  storage?: StorageConfig,
): OidcClientStore {
  const parsedConfig = oidcClientConfigSchema.parse(config);
  const { subscribe, set } = writable<OidcClient | null>(null);

  // Construct once and keep the promise. `set` runs synchronously inside the
  // `.then`, so the store is populated before `getClient()` resolves.
  const clientPromise = oidc({
    config: parsedConfig,
    requestMiddleware,
    ...(logger && { logger }),
    ...(storage && { storage }),
  })
    .then((client) => {
      set(client);
      return client;
    })
    .catch((err: unknown) => {
      const error: GenericError = {
        error: err instanceof Error ? err.message : String(err),
        type: 'unknown_error',
      };
      set(error);
      return error;
    });

  return { subscribe, getClient: () => clientPromise };
}
